import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Checkbox,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  FaCalendarAlt,
  FaPhone,
  FaIdCard,
  FaUser,
  FaHospital,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import { bookingService } from "../../../services/bookingService";
import { settingsService } from "../../../services/settingsService";
import AppointmentQR from "../AppointmentQR/AppointmentQR";
import styles from "./BookingModal.module.css";

const BookingModal = ({ open, onClose, doctors, selectedDoctor, hospital }) => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timeSlots = settingsService.getTimeSlots();

  // Có đăng ký khám BHYT hay không -> quyết định bắt buộc nhập số thẻ BHYT
  const isBhyt = Form.useWatch("isBhyt", form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setResult(null);
    }
  }, [open, form]);

  useEffect(() => {
    if (open && selectedDoctor) {
      form.setFieldsValue({ bacSy: selectedDoctor.id });
    }
  }, [selectedDoctor, open, form]);

  // Mở form -> tự điền ngày khám là hôm nay
  useEffect(() => {
    if (open) {
      form.setFieldsValue({ ngayKham: dayjs() });
    }
  }, [open, form]);

  const handleFinish = async (values) => {
    setSubmitting(true);
    try {
      const doctor = doctors.find((d) => d.id === values.bacSy);
      const bacSyName = doctor ? doctor.name : "";
      const ngayKhamDisplay = values.ngayKham
        ? values.ngayKham.format("DD/MM/YYYY")
        : null;

      // Payload theo CustomerPortalAppointmentViewModel của backend
      // Gửi ngày local (không có hậu tố Z) để tránh lệch múi giờ và lỗi
      // ghi DateTime Kind=Utc vào cột "timestamp without time zone" (PostgreSQL)
      const apiPayload = {
        hoTen: values.hoTen,
        ngaySinh: values.ngaySinh,
        soDienThoai: values.soDienThoai,
        cccd: values.cccd || "",
        isBhyt: !!values.isBhyt,
        bhyt: values.isBhyt ? values.bhyt || "" : "",
        bookingDate: values.ngayKham
          ? values.ngayKham.format("YYYY-MM-DD")
          : null,
        bookingTime: values.khungGio || "",
        doctorId: values.bacSy || null,
        hospitalId: hospital?.id || null,
        type: 1,
      };

      const res = await bookingService.createAppointment(apiPayload);
      // Gộp thông tin hiển thị cho màn QR
      setResult({
        ...res,
        hoTen: values.hoTen,
        ngayKham: ngayKhamDisplay,
        khungGio: values.khungGio || "",
        bacSy: bacSyName,
      });
    } catch (err) {
      message.error(err?.message || "Đặt lịch thất bại. Vui lòng thử lại.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    if (selectedDoctor) {
      form.setFieldsValue({ bacSy: selectedDoctor.id });
    }
  };

  const modalTitle = (
    <span className={styles.modalTitle}>
      <FaCalendarAlt className={styles.titleIcon} />
      Đặt lịch khám
    </span>
  );

  // Footer chỉ hiển thị khi đang ở form (không phải màn QR)
  const modalFooter = result ? null : (
    <Button
      type="primary"
      block
      size="large"
      loading={submitting}
      className={styles.footerSubmitBtn}
      onClick={() => form.submit()}
    >
      Lấy mã QR hẹn khám
    </Button>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={modalFooter}
      destroyOnClose
      title={modalTitle}
      centered
      width={480}
      classNames={{ body: styles.modalBody }}
      styles={{
        content: { padding: 0 },
        header: { padding: "16px 20px 8px" },
        body: { padding: "8px 20px 16px" },
        footer: { padding: "12px 20px 16px" },
      }}
    >
      {result ? (
        <AppointmentQR
          appointment={result}
          onReset={handleReset}
          onClose={onClose}
        />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
          className={styles.compactForm}
        >
          {/* Thông tin bệnh viện */}
          {hospital && (
            <div className={styles.hospitalCard}>
              {hospital.avatarUrl ? (
                <img
                  src={hospital.avatarUrl}
                  alt={hospital.name}
                  className={styles.hospitalLogo}
                />
              ) : (
                <FaHospital className={styles.hospitalLogoIcon} />
              )}
              <div className={styles.hospitalInfo}>
                <div className={styles.hospitalName}>{hospital.name}</div>
                {hospital.address && (
                  <div className={styles.hospitalMeta}>
                    <FaMapMarkerAlt className={styles.hospitalMetaIcon} />
                    <span>{hospital.address}</span>
                  </div>
                )}
                {hospital.phoneNumber && (
                  <div className={styles.hospitalMeta}>
                    <FaPhoneAlt className={styles.hospitalMetaIcon} />
                    <span>{hospital.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Thông tin cá nhân */}
          <Form.Item
            name="hoTen"
            label="Họ và tên"
            rules={[{ required: true, message: "Vui lòng nhập họ tên" }]}
          >
            <Input
              size="large"
              prefix={<FaUser />}
              placeholder="Nguyễn Văn A"
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="soDienThoai"
                label="Số điện thoại"
                rules={[
                  { required: true, message: "Vui lòng nhập số điện thoại" },
                  {
                    pattern: /^0\d{9}$/,
                    message:
                      "Số điện thoại không hợp lệ (10 số, bắt đầu bằng 0)",
                  },
                ]}
              >
                <Input
                  size="large"
                  inputMode="tel"
                  prefix={<FaPhone />}
                  placeholder="09xxxxxxxx"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="ngaySinh"
                label="Ngày sinh"
                rules={[{ required: true, message: "Vui lòng chọn ngày sinh" }]}
              >
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày sinh"
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isBhyt"
            valuePropName="checked"
            initialValue={false}
            style={{ marginBottom: 8 }}
          >
            <Checkbox>Đăng ký khám BHYT</Checkbox>
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="cccd"
                label="CCCD"
                rules={[{ required: true, message: "Vui lòng nhập CCCD" }]}
              >
                <Input
                  size="large"
                  prefix={<FaIdCard />}
                  placeholder="Nhập số CCCD"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="bhyt"
                label="Số thẻ BHYT"
                rules={
                  isBhyt
                    ? [
                        {
                          required: true,
                          message: "Vui lòng nhập số thẻ BHYT",
                        },
                      ]
                    : []
                }
              >
                <Input
                  size="large"
                  prefix={<FaIdCard />}
                  placeholder="Nhập số thẻ BHYT"
                  disabled={!isBhyt}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Thông tin lịch khám */}
          <Form.Item
            name="bacSy"
            label="Bác sỹ"
            rules={[{ required: true, message: "Vui lòng chọn bác sỹ" }]}
          >
            <Select
              size="large"
              placeholder="Chọn bác sỹ"
              options={doctors.map((d) => ({ value: d.id, label: d.name }))}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="ngayKham"
                label="Ngày khám"
                rules={[{ required: true, message: "Vui lòng chọn ngày khám" }]}
              >
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày khám"
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="khungGio"
                label="Khung giờ"
                rules={[{ required: true, message: "Vui lòng chọn khung giờ" }]}
              >
                <Select
                  size="large"
                  placeholder="Chọn khung giờ"
                  options={timeSlots.map((t) => ({ value: t, label: t }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default BookingModal;
